import React, { FC } from 'react'
import { PackageOfferingPrice } from './packageOfferingPrice'
import { PackageOfferingFeatures } from './packageOfferingFeatures'
import { PackageOfferingAction } from './packageOfferingAction'
import { useTranslation } from 'react-i18next'
import PaperWrapper from 'components/custom/PaperWrapper'
import { isThisADarkColor } from 'pages/Billing/helpers'
import CustomSvgStyled from 'components/custom/CustomSvgStyled'

interface iPackageOfferingProps {
  title: string
  labelColor: string
  labelIcon?: string
  descriptionHeading?: string
  description: string
  elevated?: boolean
  inclPrice?: boolean
  children: JSX.Element[]
  /** optional: let parent pass extra classes */
  className?: string
  cardClassName?: string
}

export const PackageOffering: FC<iPackageOfferingProps> = ({
  title,
  labelColor,
  labelIcon,
  elevated = false,
  inclPrice = true,
  children,
  className = '',
  cardClassName = '',
}) => {
  const { t } = useTranslation()

  /** Resolve Child Slots */
  let price, features, action
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) return
    if (child.type === PackageOfferingPrice) price = child
    else if (child.type === PackageOfferingFeatures) features = child
    else if (child.type === PackageOfferingAction) action = child
    else features = child
  })

  if (!action || (inclPrice && !price)) {
    return <>Make sure to include the required components.</>
  }

  // Taller when price is included; responsive min-heights
  const heightClass = inclPrice
    ? 'min-h-72 sm:min-h-80 lg:min-h-[320px]'
    : 'min-h-48 sm:min-h-56'

  return (
    <div className={`relative flex flex-col items-stretch h-full ${className}`}>
      {elevated && (
        <div className="absolute -top-5 left-0 right-0">
          <div
            className="bg-[#FFAE35] text-white text-xs sm:text-sm font-semibold py-0 px-2 
                    rounded-t-xl shadow-md text-center w-full h-full"
          >
            {t('pricing.mostPopular')}
          </div>
        </div>
      )}

      <PaperWrapper
        className={`
          flex flex-col justify-between h-full w-full
          mb-1
          ${heightClass}
          transform transition-transform
          hover:shadow-lg hover:shadow-gray-400
          cursor-default
          rounded-b-xl ${elevated ? '' : 'rounded-t-xl'}
          ${cardClassName}
        `}
      >
        <div
          className={`
            flex flex-row flex-wrap py-2 justify-center gap-2 items-center shadow-inner
            ${isThisADarkColor(labelColor) ? 'text-white' : 'text-black'}
            ${!elevated ? 'rounded-t-xl' : ''}
          `}
          style={{ backgroundColor: labelColor }}
        >
          {labelIcon !== undefined && <CustomSvgStyled src={labelIcon} />}
          <span className="text-center font-medium">{t(title)}</span>
        </div>

        <div className="flex flex-col justify-between grow p-2">
          {inclPrice && <div className="flex justify-center">{price}</div>}
          <div className="grow">{features}</div>
          <div className="flex justify-center py-2">{action}</div>
        </div>
      </PaperWrapper>
    </div>
  )
}
